import { Box, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { LegalEntityParty } from '@types'
import { validateABNorACN as validateABN } from 'utils/validateBusinessIdFormat'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { generateID } from 'utils/generateMessageID'
import { REQUEST_MESSAGE_TYPES, Request } from '../../../schemas/request'
import { useSelector } from 'react-redux'
import { RootState, dispatch } from 'redux/store'
import { PointOfContact } from './forms/types/PointOfContact'
import { FauxTextField } from './EntityRow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import ErrorIcon from '@mui/icons-material/Error'
import { clearAllLegalEntites, startLoading } from 'redux/slices/legalEntities'
import { formatBusinessId } from './forms/utils'

interface BusinessIdAutoCompleteProps {
  optionSelected: LegalEntityParty
  setLabelEntered: (value: string) => void
  setOption: (val: LegalEntityParty) => void
  label: string
  type: string
  initialValues: any
  isSelected: boolean
  clearable: boolean
  resetIssuer?: React.RefObject<any>
  resetTermsRef?: React.RefObject<any>
  handleClear: (type: string) => void
  setShowPointOfContact: (show: boolean) => void
  setPOC?: (value: PointOfContact | null) => void
  setValue: (val: LegalEntityParty) => void
  disabled?: boolean
  singleEntry?: boolean
  //pocFirstNameRef?: React.RefObject<HTMLInputElement>
  error?: boolean
  helperText?: any
}

const clean = (s: string) => (s || '').replace(/\s+/g, '')
const detectType = (cleaned: string): 'ABN' | 'ACN' => (cleaned.length === 9 ? 'ACN' : 'ABN')

const BusinessIdAutoComplete = ({
  setLabelEntered,
  setOption,
  optionSelected,
  label,
  type,
  setValue,
  setShowPointOfContact,
  setPOC,
  disabled,
  singleEntry,
  error: formError,
  helperText: formHelperText
}: BusinessIdAutoCompleteProps) => {
  const [abn, setAbn] = useState('') // DISPLAY value (may include spaces)
  const [error, setError] = useState('')
  const [lastSubmitted, setLastSubmitted] = useState('') // CLEANED
  const [lastVerifiedClean, setLastVerifiedClean] = useState('') // CLEANED
  const [abnValid, setAbnValid] = useState(false) // server-verified valid
  const [abnRejected, setAbnRejected] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false) // Loading state for beneficiaries
  const [detectedType, setDetectedType] = useState<'ABN' | 'ACN'>('ABN') // For label display
  const [responseStatus, setResponseStatus] = useState<{
    statusCode?: number
    description?: string
  }>()
  const [matchedEntity, setMatchedEntity] = useState<LegalEntityParty | null>(null)
  const [responseTriggerId, setResponseTriggerId] = useState<string>('') // Explicitly track response updates

  const legalEntities = useSelector((state: RootState) => state.legalEntities.entities)
  const statusByRequestId = useSelector((state: RootState) => state.legalEntities.statusByRequestId)
  const isLoading = useSelector((state: RootState) => state.legalEntities.isLoading)
  const Status = useSelector((state: RootState) => state.legalEntities.Status)

  const { onSocketSend } = useSocketConnection()
  const requestIdRef = useRef<string | null>(null)
  const abnInputRef = useRef<HTMLInputElement | null>(null)
  const abnTypeRef = useRef<'ABN' | 'ACN'>('ABN')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Keep the label in sync with the current display value while typing
  useEffect(() => {
    const detected = detectType(clean(abn))
    abnTypeRef.current = detected
    setDetectedType(detected)
  }, [abn])

  // Reset ALL state when type changes - critical for shared component instances
  useEffect(() => {
    setAbn('')
    setError('')
    setLastSubmitted('')
    setLastVerifiedClean('')
    setAbnValid(false)
    setAbnRejected(false)
    setIsVerifying(false)
    setResponseStatus(undefined)
    setMatchedEntity(null)
    setResponseTriggerId('')
    requestIdRef.current = null
    abnTypeRef.current = 'ABN'
  }, [type])

  // Watch this request's status only
  useEffect(() => {
    if (!requestIdRef.current) return
    const status = statusByRequestId?.[requestIdRef.current]
    if (status) {
      setResponseStatus(status)
      // Explicitly trigger UI update by changing this ID whenever response arrives
      setResponseTriggerId(requestIdRef.current)
    }
  }, [statusByRequestId])

  // Handle server response
  useEffect(() => {
    if (!responseStatus || !lastSubmitted || !responseTriggerId) return

    // Only handle responses for beneficiaries - exit early for other types
    if (type !== 'beneficiaries') return

    // IMPORTANT: Clear the timeout immediately when ANY response arrives
    // This prevents the timeout message from showing when server actually responds
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    // Clear verifying state
    setIsVerifying(false)

    // Handle any error response (status code is not 1023 which is success)
    if (responseStatus.statusCode && responseStatus.statusCode !== 1023) {
      // Show error inline for all errors
      let message = responseStatus.description || 'ABN/ACN not Found'
      
      // For not-active errors, add explicit instruction
      if (responseStatus.statusCode === 9024 || responseStatus.statusCode === 9023 || responseStatus.statusCode === 9022) {
        message = `${responseStatus.description}. Please remove this value and click outside the field to restore your previous entry.`
      }
      
      setError(message)
      setAbnValid(false)
      setAbnRejected(true)
      return
    }

    // Try to match returned entity by the exact code we submitted (cleaned)
    const match = legalEntities.find((entity) =>
      entity.legalEntityCodes?.some(
        (code) =>
          (code.legalEntityCodeType === 'ABN' || code.legalEntityCodeType === 'ACN') &&
          clean(code.legalEntityCodeData) === lastSubmitted
      )
    )

    if (responseStatus.statusCode === 1023 && match) {
      // VERIFIED SUCCESS
      setMatchedEntity(match)
      setOption(match)
      setValue(match)
      setLabelEntered(match.commonName)

      setLastVerifiedClean(lastSubmitted) // persist the last verified, CLEANED
      setAbn(formatBusinessId(lastSubmitted)) // display formatted
      setError('')
      setAbnValid(true)
      setAbnRejected(false)

      if (match.onboarded) {
        setShowPointOfContact(false)
        setPOC?.(null)
      } else {
        setShowPointOfContact(true)
      }
    } else if (responseStatus.statusCode !== 1023) {
      // VERIFIED FAILURE (server rejected, including 9023)
      // For beneficiaries, show specific error message
      const errorMsg =
        type === 'beneficiaries'
          ? 'This business ID could not be verified'
          : responseStatus.description || 'Business ID could not be verified'
      setError(errorMsg)
      setAbnValid(false)
      setAbnRejected(true)
      // revert to last verified value (if any), otherwise clear
      setAbn(lastVerifiedClean || '')
    }
  }, [responseStatus, lastSubmitted, legalEntities, responseTriggerId])

  useEffect(() => {
    const cleaned = clean(abn)
    if (!cleaned) {
      setError('')
      return
    }

    const type = detectType(cleaned)
    abnTypeRef.current = type

    // On typing, we don't validate or submit
    // Clear error while typing to allow user to enter without seeing errors
    // Actual validation happens on blur or Enter key only
    if (!lastVerifiedClean) {
      // No verified value exists yet, don't show error while typing
      setError('')
      setAbnValid(false)
      setAbnRejected(false)
      return
    }

    // If there's a previously verified value and user is typing something different,
    // just clear the error and wait for blur/Enter to re-validate
    setError('')
    setAbnValid(false)
    setAbnRejected(false)
  }, [abn, lastVerifiedClean]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (skipFormatValidation = false) => {
    const cleaned = clean(abn)
    abnTypeRef.current = detectType(cleaned)

    // Format validation first (unless already validated by caller)
    if (!skipFormatValidation && !validateABN(cleaned)) {
      const detectedType = detectType(cleaned)
      const errorMsg =
        cleaned.length < 9 ? 'Not a valid ABN/ACN code' : `Not a valid ${detectedType} code`
      setError(errorMsg)
      setAbnValid(false)
      setAbnRejected(false)
      // Revert immediately to last verified (do not leave invalid value around)
      setAbn(lastVerifiedClean || '')
      return
    }

    // Avoid duplicate submits
    if (cleaned === lastVerifiedClean || (cleaned === lastSubmitted && isLoading)) return

    setLastSubmitted(cleaned)
    requestIdRef.current = generateID()
    dispatch(clearAllLegalEntites())
    dispatch(startLoading())
    setResponseStatus(undefined) // Clear old response to prevent stale data from triggering old popup
    setResponseTriggerId('') // Reset trigger ID to ensure fresh update when response arrives

    const request: Request = {
      messageId: requestIdRef.current,
      messageType: REQUEST_MESSAGE_TYPES.QueryLegalEntities,
      QueryLegalEntities: {
        legalEntityCode: {
          legalEntityCodeData: cleaned,
          legalEntityCodeType: abnTypeRef.current
        }
      }
    }

    onSocketSend(request)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      // Timeout: mark as rejected and revert to last verified
      setIsVerifying(false)
      const timeoutMsg =
        type === 'beneficiaries'
          ? 'Verification timed out. Please try again.'
          : Status?.description || 'Verification timed out'
      setError(timeoutMsg)
      setAbnValid(false)
      setAbnRejected(true)
      setAbn(lastVerifiedClean ? formatBusinessId(lastVerifiedClean) : '')
    }, 15000) // Increased timeout to 15 seconds for slower network/server responses
  }

  // Initialize from a pre-selected option (for non-beneficiary flows or pre-populated data)
  useEffect(() => {
    if (!optionSelected?.legalEntityIdentifier) return

    // Only update if we don't have a matched entity from server response
    if (matchedEntity?.legalEntityIdentifier) return

    const abnCode = optionSelected.legalEntityCodes?.find(
      (c) => c.legalEntityCodeType === 'ABN' || c.legalEntityCodeType === 'ACN'
    )?.legalEntityCodeData

    if (abnCode) {
      const cleaned = clean(abnCode)
      setAbn(formatBusinessId(cleaned))
      setLastVerifiedClean(cleaned)
      setLastSubmitted(cleaned)
      setAbnValid(true)
      setAbnRejected(false)
      setError('')

      // Clear loading state since we're using a pre-verified value, not making a new request
      dispatch(clearAllLegalEntites())
    }

    // Always update PointOfContact visibility based on current onboarded status
    const isOnboarded = optionSelected.onboarded === true
    setShowPointOfContact(!isOnboarded)
    if (isOnboarded) {
      setPOC?.(null)
    }
  }, [optionSelected, matchedEntity])

  // When server verifies an entity (matchedEntity changes), update POC visibility based on server response
  useEffect(() => {
    if (!matchedEntity?.legalEntityIdentifier) return

    const isOnboarded = matchedEntity.onboarded === true
    setShowPointOfContact(!isOnboarded)
    if (isOnboarded) {
      setPOC?.(null)
    }
  }, [matchedEntity?.legalEntityIdentifier, matchedEntity?.onboarded])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleBlur = () => {
    const cleaned = clean(abn)

    // If we already have a verified value and user changed it
    if (lastVerifiedClean) {
      if (cleaned !== lastVerifiedClean) {
        // User edited the verified value

        // If field is now empty, revert to last verified
        if (!cleaned) {
          setAbn(formatBusinessId(lastVerifiedClean))
          setError('')
          setAbnValid(true)
          setAbnRejected(false)
          return
        }

        // User entered something different - validate format
        const type_detected = detectType(cleaned)
        abnTypeRef.current = type_detected
        setLastSubmitted(cleaned)

        if (!validateABN(cleaned)) {
          // Format is invalid - revert to last verified value
          setAbn(formatBusinessId(lastVerifiedClean))
          setError('')
          setAbnValid(true)
          setAbnRejected(false)
        } else {
          // Format is valid - submit to server for verification
          setError('')
          if (type === 'beneficiaries') {
            setIsVerifying(true)
          }
          handleSubmit()
        }
      } else {
        // same number, just ensure formatting
        setAbn(formatBusinessId(lastVerifiedClean))
      }
      return
    }

    // No previously verified value exists
    if (!cleaned) {
      // Field is empty
      setError('')
      return
    }

    // Validate format and submit to server if valid
    const type_detected = detectType(cleaned)
    abnTypeRef.current = type_detected

    if (!validateABN(cleaned)) {
      // Format is invalid, show error
      const errorMsg =
        cleaned.length < 9 ? 'Not a valid ABN/ACN code' : `Not a valid ${type_detected} code`
      setError(errorMsg)
      setAbnValid(false)
      setAbnRejected(false)
    } else {
      // Format is valid - submit to server for verification
      setError('')
      if (type === 'beneficiaries') {
        setIsVerifying(true)
      }
      handleSubmit()
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return

    const cleaned = clean(abn)

    // If we already have a verified value
    if (lastVerifiedClean) {
      if (cleaned !== lastVerifiedClean) {
        // User changed the verified value

        // If field is now empty, revert to last verified
        if (!cleaned) {
          setAbn(formatBusinessId(lastVerifiedClean))
          setError('')
          setAbnValid(true)
          setAbnRejected(false)
          return
        }

        const type_detected = detectType(cleaned)
        abnTypeRef.current = type_detected

        if (!validateABN(cleaned)) {
          const errorMsg =
            cleaned.length < 9 ? 'Not a valid ABN/ACN code' : `Not a valid ${type_detected} code`
          setError(errorMsg)
          setAbnValid(false)
          setAbnRejected(false)
          return
        }

        // Format is valid - submit to server for verification
        setError('')
        if (type === 'beneficiaries') {
          setIsVerifying(true)
        }
        setLastSubmitted(cleaned)
        handleSubmit(true) // true = skip format validation, already done above
      } else {
        // same number, just ensure formatting
        setAbn(formatBusinessId(lastVerifiedClean))
      }
      return
    }

    // No previously verified value exists - this is first-time entry
    // Validate format first
    if (!cleaned) {
      // For beneficiaries, show error; for others, just return
      if (type === 'beneficiaries') {
        setError('Business ID is required for beneficiaries')
      }
      return
    }

    const type_detected = detectType(cleaned)
    abnTypeRef.current = type_detected

    if (!validateABN(cleaned)) {
      const errorMsg =
        cleaned.length < 9 ? 'Not a valid ABN/ACN code' : `Not a valid ${type_detected} code`
      setError(errorMsg)
      setAbnValid(false)
      setAbnRejected(false)
      return
    }

    // Clear any previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Format is valid, proceed with server verification
    setError('')
    if (type === 'beneficiaries') {
      setIsVerifying(true)
    }
    handleSubmit(true) // true = skip format validation, already done above
  }

  return (
    <Box>
      {singleEntry || (type === 'issuers' && abn) ? (
        <FauxTextField
          label={`${label} ${detectedType}`}
          value={abn}
          showSuccess={!!abn}
          singleEntry
        />
      ) : (
        <TextField
          label={
            type === 'beneficiaries' && !abn
              ? 'Enter beneficiary ABN/ACN to search'
              : `${label} ${detectedType} `
          }
          fullWidth
          value={abn}
          autoComplete="off"
          sx={{ width: '98%' }}
          inputRef={abnInputRef}
          onChange={(e) => {
            e.preventDefault()
            let value = e.target.value

            // Keep only digits and spaces
            value = value.replace(/[^0-9\s]/g, '')

            // Normalise spaces (no double spaces, no leading space)
            value = value.replace(/\s{2,}/g, ' ').replace(/^\s+/, '')

            // Enforce max 11 digits (ABN is 11, ACN is 9 – we cap at 11)
            const digitsOnly = value.replace(/\s/g, '')
            if (digitsOnly.length > 11) {
              // Block input if it would exceed 11 digits
              return
            }

            // Clear any pending timeout when user edits
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
              timeoutRef.current = null
            }

            setAbn(value)
            setError('')
            setAbnValid(false) // typed value is not yet server-verified
            setAbnRejected(false)
          }}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          error={!!error || (type === 'beneficiaries' && !!formError)}
          helperText={error || (type === 'beneficiaries' && formHelperText)}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {(isLoading || isVerifying) && type === 'beneficiaries' && abn ? (
                  <CircularProgress size={20} />
                ) : error || (type === 'beneficiaries' && formError) ? (
                  <Tooltip title={error || formHelperText}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: '8px',
                        mr: '4px'
                      }}
                    >
                      <ErrorIcon
                        sx={{
                          color: '#d32f2f',
                          fontSize: '20px',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  </Tooltip>
                ) : !disabled && abnValid && !error && !lastVerifiedClean ? (
                  <>
                    <Tooltip title="Edit ABN/ACN">
                      <Box
                        onClick={() => {
                          setAbnValid(false) // leave verified state when editing
                          setTimeout(() => abnInputRef.current?.focus(), 0)
                        }}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          ml: '8px',
                          mr: '4px'
                        }}
                      >
                        <EditIcon />
                      </Box>
                    </Tooltip>
                    <Box sx={{ ml: '8px', mr: '4px' }}>
                      <Tooltip title="Valid ABN/ACN found">
                        <CheckCircleIcon color="success" />
                      </Tooltip>
                    </Box>
                  </>
                ) : !disabled && abnValid && !error && lastVerifiedClean ? (
                  <Box sx={{ ml: '8px', mr: '4px' }}>
                    <Tooltip title="ABN/ACN verified and locked">
                      <CheckCircleIcon color="success" />
                    </Tooltip>
                  </Box>
                ) : null}
              </InputAdornment>
            )
          }}
        />
      )}
    </Box>
  )
}

export default BusinessIdAutoComplete
